/**
 * Automated preparation review API — W15-E02-006 Ready to Send.
 */

import api from "../../../shared/api.jsx";

/**
 * @param {string} quoteId
 * @returns {Promise<{ success: boolean, data?: object, error?: string, code?: string }>}
 */
export const fetchAutomatedPreparationReview = async (quoteId) => {
  try {
    const response = await api.get(
      `/quotes/${quoteId}/automated-preparation-review`
    );
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};

/**
 * @param {string} quoteId
 * @param {{ approvedBy?: string }} [body]
 * @returns {Promise<{ success: boolean, data?: object, error?: string, code?: string }>}
 */
export const repriceAutomatedPreparation = async (quoteId) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/automated-preparation-reprice`,
      {}
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Re-run failed",
        code: response.code,
      };
    }
    return {
      success: true,
      data: response.data || response,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};

/**
 * @param {string} quoteId
 * @param {{ approvedBy?: string }} [body]
 * @returns {Promise<{ success: boolean, data?: object, error?: string, code?: string }>}
 */
export const approveAutomatedPreparation = async (quoteId, body = {}) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/automated-preparation-approve`,
      body
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Approval failed",
        code: response.code,
      };
    }
    return {
      success: true,
      data: response.data || response,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};

/**
 * Resolve quote total for display/edit — never add estimatedCost on top of line items.
 * @param {number} itemsTotal
 * @param {number|string|null|undefined} estimatedCost
 * @returns {number}
 */
export function resolveQuoteDisplayTotal(itemsTotal, estimatedCost) {
  const manual = estimatedCost ? parseFloat(String(estimatedCost)) : 0;
  if (manual > 0) return manual;
  return itemsTotal;
}

/**
 * Adopt pricing API — commit preparation suggestion onto quote commercial fields.
 * @param {string} quoteId
 */
export const adoptAutomatedPreparationPricing = async (quoteId) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/automated-preparation-adopt-pricing`,
      {}
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Adopt pricing failed",
        code: response.code,
      };
    }
    return {
      success: true,
      data: response.data || response,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};

/**
 * Adopt catalogue|n8n|ai enrichment report as authoritative (D-015 / W15-E02-010).
 * @param {string} quoteId
 * @param {{ source: "catalogue"|"n8n"|"ai", adoptedBy?: string, reason?: string }} body
 */
export const adoptAutomatedPreparationEstimate = async (quoteId, body = {}) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/automated-preparation-adopt-estimate`,
      body
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Adopt failed",
        code: response.code,
      };
    }
    return {
      success: true,
      data: response.data || response,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};

/**
 * Ops reconcile: match a preparation inventory line to a catalogue SKU.
 * @param {string} quoteId
 * @param {string} lineKey sourceItemId or review item key
 * @param {{ catalogueSkuId: string, matchedBy?: string, updateLeadDescription?: boolean }} body
 */
export const matchCatalogueLineForPreparation = async (
  quoteId,
  lineKey,
  body = {}
) => {
  try {
    const encodedKey = encodeURIComponent(lineKey);
    const response = await api.post(
      `/quotes/${quoteId}/automated-preparation-inventory/${encodedKey}/match-catalogue`,
      body
    );
    if (response.success === false) {
      return {
        success: false,
        error: response.error || response.message || "Catalogue match failed",
        code: response.code,
      };
    }
    return {
      success: true,
      data: response.data || response,
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message,
      code: error.response?.data?.code,
    };
  }
};
